import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    Output,
    ViewChild
} from '@angular/core';
import {Location} from "@flowmaps-com/flowmaps-typescriptmodels";
import type {
    GeoJSONSource,
    Map as MapLibreMap
} from 'maplibre-gl';
import {AppCommonUtils} from "../../../common/app-common-utils";
import {DashboardContext} from "../dashboard.context";

const LOCATION_SOURCE_ID = 'dashboard-locations';
const CLUSTER_LAYER_ID = 'dashboard-location-clusters';
const CLUSTER_COUNT_LAYER_ID = 'dashboard-location-cluster-count';
const CLUSTER_SHADOW_LAYER_ID = 'dashboard-location-cluster-shadow';
const LOCATION_LAYER_ID = 'dashboard-location';
// The public OpenFreeMap style requires no API key and supplies its own attribution metadata.
const MAP_STYLE_URL = 'https://tiles.openfreemap.org/styles/positron';
const DEFAULT_CENTER: [number, number] = [5.1670, 52.1595];
const DEFAULT_ZOOM = 5.2;
type MapLibreModule = typeof import('maplibre-gl');
type LazyMapLibreModule = MapLibreModule & {default?: MapLibreModule};

@Component({
    selector: 'app-dashboard-maps',
    templateUrl: './dashboard-maps.component.html',
    styleUrls: ['./dashboard-maps.component.scss']
})
export class DashboardMapsComponent implements AfterViewInit, OnDestroy {
    private _locations: Location[] = [];
    private map?: MapLibreMap;
    private mapLibre?: MapLibreModule;
    private resizeObserver?: ResizeObserver;
    private destroyed = false;

    @ViewChild('mapContainer', {static: true}) private mapContainer: ElementRef<HTMLDivElement>;
    @ViewChild('locationCountText', {static: true}) private locationCountText: ElementRef<HTMLSpanElement>;
    @Output() visibleLayers: EventEmitter<Location[]> = new EventEmitter<Location[]>();

    locationCountLabel = '0';
    locationCountUnit = AppCommonUtils.getPreferredLanguage() === 'nl' ? 'locaties' : 'locations';
    resetMapLabel = AppCommonUtils.getPreferredLanguage() === 'nl'
        ? 'Terug naar Nederland'
        : 'Reset map view';

    constructor(private ngZone: NgZone) {
    }

    ngAfterViewInit(): void {
        this.renderLocationCount();
        this.ngZone.runOutsideAngular(async () => {
            const importedMapLibre = await import('maplibre-gl') as LazyMapLibreModule;
            // Development exposes named exports, while Angular's optimized CommonJS chunk exposes a default export.
            const mapLibre = importedMapLibre.default ?? importedMapLibre;
            if (this.destroyed) {
                return;
            }

            this.mapLibre = mapLibre;
            this.map = new mapLibre.Map({
                container: this.mapContainer.nativeElement,
                style: MAP_STYLE_URL,
                center: DEFAULT_CENTER,
                zoom: DEFAULT_ZOOM,
                maxZoom: 18,
                attributionControl: false
            });

            this.map.addControl(new mapLibre.NavigationControl({
                showCompass: false,
                showZoom: true
            }), 'top-left');
            this.map.addControl(new mapLibre.AttributionControl({
                compact: false
            }), 'bottom-right');
            this.resizeObserver = new ResizeObserver(() => this.map?.resize());
            this.resizeObserver.observe(this.mapContainer.nativeElement);

            this.map.on('load', () => {
                this.applyMapPalette();
                this.applyMapLanguage();
                this.addLocationLayers();
                this.updateLocationData();
                this.emitVisibleLayers();
            });
            this.map.on('moveend', () => this.emitVisibleLayers());
        });
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.resizeObserver?.disconnect();
        this.resizeObserver = undefined;
        this.map?.remove();
        this.map = undefined;
        this.mapLibre = undefined;
    }

    @Input()
    set locations(locations: Location[]) {
        this._locations = locations || [];
        const locationCount = this.mappableLocations.length;
        this.locationCountLabel = new Intl.NumberFormat(
            this.preferredLanguage === 'nl' ? 'nl-NL' : 'en-GB'
        ).format(locationCount);
        this.locationCountUnit = this.preferredLanguage === 'nl'
            ? locationCount === 1 ? 'locatie' : 'locaties'
            : locationCount === 1 ? 'location' : 'locations';
        this.renderLocationCount();

        if (this.map?.isStyleLoaded()) {
            this.ngZone.runOutsideAngular(() => {
                this.updateLocationData();
                this.emitVisibleLayers();
            });
        }
    }

    resetMapView(): void {
        this.map?.easeTo({
            center: DEFAULT_CENTER,
            zoom: DEFAULT_ZOOM,
            bearing: 0,
            pitch: 0,
            duration: 400
        });
    }

    private get mappableLocations(): Location[] {
        return this._locations.filter(location => {
            const geoLocation = location?.info?.address?.geoLocation;
            return Number.isFinite(geoLocation?.latitude) && Number.isFinite(geoLocation?.longitude);
        });
    }

    private applyMapPalette(): void {
        const paintProperties: Array<[string, string, string]> = [
            ['background', 'background-color', '#fbfaf6'],
            ['water', 'fill-color', '#d9eaf3'],
            ['park', 'fill-color', '#edf3e9'],
            ['landuse_residential', 'fill-color', '#f5f4ef'],
            ['landcover_wood', 'fill-color', '#e8f0e4'],
            ['road_area_pier', 'fill-color', '#fbfaf6']
        ];

        paintProperties.forEach(([layerId, property, value]) => {
            if (this.map.getLayer(layerId)) {
                this.map.setPaintProperty(layerId, property, value);
            }
        });
    }

    private applyMapLanguage(): void {
        const language = this.preferredLanguage;
        const localizedName = [
            'coalesce',
            ['get', `name:${language}`],
            ['get', `name_${language}`],
            ['get', 'name:latin'],
            ['get', 'name']
        ];

        this.map.getStyle().layers
            .filter(layer => layer.type === 'symbol'
                && 'source-layer' in layer
                && layer['source-layer'] === 'place')
            .forEach(layer => this.map.setLayoutProperty(layer.id, 'text-field', localizedName));
    }

    private get preferredLanguage(): 'nl' | 'en' {
        return AppCommonUtils.getPreferredLanguage() === 'nl' ? 'nl' : 'en';
    }

    private renderLocationCount(): void {
        if (this.locationCountText) {
            this.locationCountText.nativeElement.textContent =
                `${this.locationCountLabel} ${this.locationCountUnit}`;
        }
    }

    private addLocationLayers(): void {
        if (!this.map || this.map.getSource(LOCATION_SOURCE_ID)) {
            return;
        }

        this.map.addSource(LOCATION_SOURCE_ID, {
            type: 'geojson',
            data: this.locationFeatureCollection,
            cluster: true,
            clusterMaxZoom: 14,
            clusterRadius: 54
        });

        this.map.addLayer({
            id: CLUSTER_SHADOW_LAYER_ID,
            type: 'circle',
            source: LOCATION_SOURCE_ID,
            filter: ['has', 'point_count'],
            paint: {
                'circle-color': '#0f2f48',
                'circle-opacity': 0.14,
                'circle-translate': [0, 3],
                'circle-radius': [
                    'step',
                    ['get', 'point_count'],
                    16,
                    10,
                    19,
                    100,
                    23
                ]
            }
        });

        this.map.addLayer({
            id: CLUSTER_LAYER_ID,
            type: 'circle',
            source: LOCATION_SOURCE_ID,
            filter: ['has', 'point_count'],
            paint: {
                'circle-color': [
                    'step',
                    ['get', 'point_count'],
                    '#21b99b',
                    10,
                    '#f2bd18',
                    100,
                    '#ed7628'
                ],
                'circle-radius': [
                    'step',
                    ['get', 'point_count'],
                    14,
                    10,
                    17,
                    100,
                    21
                ],
                'circle-stroke-width': 2,
                'circle-stroke-color': '#ffffff',
                'circle-opacity': 0.96
            }
        });

        this.map.addLayer({
            id: CLUSTER_COUNT_LAYER_ID,
            type: 'symbol',
            source: LOCATION_SOURCE_ID,
            filter: ['has', 'point_count'],
            layout: {
                'text-field': ['to-string', ['get', 'point_count']],
                'text-size': [
                    'step',
                    ['get', 'point_count'],
                    11,
                    100,
                    12
                ],
                'text-font': ['Noto Sans Regular']
            },
            paint: {
                'text-color': '#ffffff',
                'text-halo-color': 'rgba(0, 0, 0, 0.08)',
                'text-halo-width': 0.5
            }
        });

        this.map.addLayer({
            id: LOCATION_LAYER_ID,
            type: 'circle',
            source: LOCATION_SOURCE_ID,
            filter: ['!', ['has', 'point_count']],
            paint: {
                'circle-color': '#16445f',
                'circle-radius': 7,
                'circle-stroke-width': 3,
                'circle-stroke-color': '#ffffff'
            }
        });

        this.bindMapInteractions();
    }

    private bindMapInteractions(): void {
        this.map.on('click', CLUSTER_LAYER_ID, async event => {
            const feature = this.map.queryRenderedFeatures(event.point, {layers: [CLUSTER_LAYER_ID]})[0];
            const clusterId = Number(feature?.properties?.['cluster_id']);
            const coordinates = feature?.geometry?.type === 'Point' ? feature.geometry.coordinates : undefined;
            const source = this.map.getSource(LOCATION_SOURCE_ID) as GeoJSONSource;

            if (!coordinates || !Number.isFinite(clusterId)) {
                return;
            }

            const zoom = await source.getClusterExpansionZoom(clusterId);
            this.map.easeTo({
                center: [coordinates[0], coordinates[1]],
                zoom,
                duration: 250
            });
        });

        this.map.on('click', LOCATION_LAYER_ID, event => {
            const feature = event.features?.[0];
            const coordinates = feature?.geometry?.type === 'Point' ? feature.geometry.coordinates : undefined;
            if (!coordinates) {
                return;
            }

            const popupContent = document.createElement('div');
            popupContent.className = 'location-map-popup';

            const name = document.createElement('strong');
            name.textContent = feature.properties?.['name'] || 'Locatie';
            popupContent.appendChild(name);

            const addressLine = [
                feature.properties?.['street'],
                feature.properties?.['number'],
                feature.properties?.['addition']
            ].filter(Boolean).join(' ');
            const cityLine = [
                feature.properties?.['zipCode'],
                feature.properties?.['city']
            ].filter(Boolean).join(' ');

            if (addressLine || cityLine) {
                const address = document.createElement('span');
                address.className = 'location-map-popup-address';
                [addressLine, cityLine].filter(Boolean).forEach(line => {
                    const addressRow = document.createElement('span');
                    addressRow.textContent = line;
                    address.appendChild(addressRow);
                });
                popupContent.appendChild(address);
            }

            const locationId = feature.properties?.['locationId'];
            if (locationId) {
                const selectButton = document.createElement('button');
                selectButton.type = 'button';
                selectButton.className = 'location-map-popup-action';
                selectButton.textContent = this.preferredLanguage === 'nl'
                    ? 'Selecteer locatie'
                    : 'Select location';
                selectButton.addEventListener('click', () => this.selectLocation(locationId));
                popupContent.appendChild(selectButton);
            }

            new this.mapLibre.Popup({offset: 14, closeButton: false, maxWidth: '17rem'})
                .setLngLat([coordinates[0], coordinates[1]])
                .setDOMContent(popupContent)
                .addTo(this.map);
        });

        [CLUSTER_LAYER_ID, LOCATION_LAYER_ID].forEach(layerId => {
            this.map.on('mouseenter', layerId, () => {
                this.map.getCanvas().style.cursor = 'pointer';
            });
            this.map.on('mouseleave', layerId, () => {
                this.map.getCanvas().style.cursor = '';
            });
        });
    }

    private updateLocationData(): void {
        const source = this.map?.getSource(LOCATION_SOURCE_ID) as GeoJSONSource;
        source?.setData(this.locationFeatureCollection);
    }

    private get locationFeatureCollection() {
        return {
            type: 'FeatureCollection' as const,
            features: this.mappableLocations.map(location => {
                const geoLocation = location.info.address.geoLocation;
                return {
                    type: 'Feature' as const,
                    id: location.locationId,
                    properties: {
                        locationId: location.locationId,
                        name: location.info.name,
                        street: location.info.address.street,
                        number: location.info.address.number,
                        addition: location.info.address.addition,
                        zipCode: location.info.address.zipCode,
                        city: location.info.address.city
                    },
                    geometry: {
                        type: 'Point' as const,
                        coordinates: [geoLocation.longitude, geoLocation.latitude]
                    }
                };
            })
        };
    }

    private selectLocation(locationId: string): void {
        const locationDashboard = DashboardContext.locationDashboard(locationId);
        const dashboardUrl = '/dashboard/' + DashboardContext.dashboardToBase64(locationDashboard);
        this.ngZone.run(() => AppCommonUtils.navigateToUrl(dashboardUrl));
    }

    private emitVisibleLayers(): void {
        if (!this.map) {
            return;
        }

        const bounds = this.map.getBounds();
        const foundLocations = this.mappableLocations.filter(location => {
            const geoLocation = location.info.address.geoLocation;
            return bounds.contains([geoLocation.longitude, geoLocation.latitude]);
        });
        this.visibleLayers.emit(foundLocations);
    }
}
